import { selectProduct } from "@/store/slices/product";
import React, { useState, useEffect, useMemo, useRef } from "react";
import Image from "next/image";
import { useSelector } from "react-redux";
import styles from "./made-in-india-share.module.scss";
import share from "../../../assets/images/share.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as faHeartSolid } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { useAppDispatch } from "@/store";
import { authActions, selectAuth } from "@/store/slices/auth";
import { fetchWishlistItems, selectWishlist } from "@/store/slices/wishlist";
import endpoints from "@/api/endpoints";
import httpClient from "@/api/http-client";
import { toast } from "react-toastify";
import LoadingSpinnerSmall from "@/components/common/loading-spinner-small";
import { fbqTrackEvent } from "@/lib/fpixel";
import { unbxdCusEngTrackEvent } from "@/lib/unbxd";

const ProductDetailsMadeInIndiaShare = () => {
  const productState = useSelector(selectProduct);
  const dispatch = useAppDispatch();
  const wishlistState = useSelector(selectWishlist);
  const authState = useSelector(selectAuth);
  const actionInProgress = useRef(false);
  const [wishlistActionLoading, setwishlistActionLoading] = useState(false);
  const [actionData, setActionData] = useState({ type: "", id: "" });

  const wishlistItem = useMemo(
    () =>
      wishlistState.items.find((v) => v.id == productState.data?.id) || null,
    [wishlistState.items, productState.data]
  );

  const addToWishlist = async () => {
    if (wishlistActionLoading) return;
    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      setActionData({
        type: "wishlist",
        id: `${productState?.data?.id || ""}`,
      });
      return;
    }
    setwishlistActionLoading(true);
    try {
      await httpClient.post(endpoints.wishlist.add(), {
        product_id: productState.data?.id,
        user_id: authState.user.id,
      });
      await dispatch(fetchWishlistItems(authState.user.id as any));
      toast("Item added in wishlist", {
        type: "success",
        toastId: "withlist-add-success",
      });
      fbqTrackEvent("AddToWishlist", {
        content_type: "product",
        content_ids: [productState.data?.sku],
      });
      if (window.smartech) {
        unbxdCusEngTrackEvent("Add to wishlist ", {
          source: "web",
          platform: "senco",
          prid: productState.data?.id,
          variant_id: productState.selectedPrice?.id,
          image: productState.data?.images[0].media,
          prqt: productState.selectedPrice?.stock || 1,
          product_name: productState.data?.title,
          amount: productState.selectedPrice?.total,
          product_url: `${window.location.origin}/jewellery/${productState.data?.slug}`,
          discounted_price: productState.selectedPrice?.discount?.total || 0,
          stock_availability: productState.selectedPrice?.stock ? 1 : 0,
        });
      }
    } catch (error) {}
    setwishlistActionLoading(false);
  };

  const removeFromWishlist = async () => {
    if (wishlistActionLoading) return;
    if (!confirm("Are you sure you want to remove this product from wishlist?"))
      return;
    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      return;
    }
    setwishlistActionLoading(true);
    try {
      await httpClient.post(endpoints.wishlist.remove(), {
        product_id: productState.data?.id,
        user_id: authState.user.id,
      });
      await dispatch(fetchWishlistItems(authState.user.id as any));
      toast("Item removed from wishlist", {
        type: "success",
        toastId: "withlist-remove-success",
      });
    } catch (error) {}
    setwishlistActionLoading(false);
  };

  const shareProduct = (platform = "native") => {
    const productTitle = productState.data?.title || "Check this out!";
    const productUrl = location.href;
    const encodedUrl = encodeURIComponent(productUrl);
    const encodedText = encodeURIComponent(
      `Look at this amazing product I found: ${productTitle} - ${productUrl}`
    );

    const platformLinks = {
      whatsapp: `https://wa.me/?text=${encodedText}`,
      email: `mailto:?subject=${encodeURIComponent(
        productTitle
      )}&body=${encodedText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent(
        productTitle
      )}`,
      copy: null,
    };

    if (platform === "native" && navigator.share) {
      navigator
        .share({
          title: productTitle,
          text: "Look at this amazing product I found!",
          url: productUrl,
        })
        .then(() => {
          toast("Product shared successfully!", {
            type: "success",
            toastId: "share-success",
          });
        })
        .catch((error) => {
          console.error("Error sharing:", error);
        });
      return;
    }

    if (platformLinks[platform as keyof typeof platformLinks]) {
      window.open(
        platformLinks[platform as keyof typeof platformLinks]!,
        "_blank"
      );
      return;
    }

    if (platform === "copy" || !navigator.share) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(productUrl);
        toast("Product link copied to clipboard", {
          type: "success",
          toastId: "copy-link-success",
        });
      }
    }
  };

  useEffect(() => {
    const handleAction = async () => {
      if (actionInProgress.current) return;

      if (actionData.id && authState.user) {
        actionInProgress.current = true;

        try {
          if (actionData.type === "wishlist") {
            await addToWishlist();
          }
        } catch (error) {
          console.error("Error handling action:", error);
        } finally {
          setActionData({ type: "", id: "" });
          actionInProgress.current = false;
        }
      }
    };
    if (actionData.id && authState.user) {
      handleAction();
    }
  }, [authState, actionData]);

  if (!productState.data) {
    return <></>;
  }

  return (
    <div
      className={`d-flex justify-content-between align-items-center mb-1 w-100`}
    >
      <div className={styles.h4}>
        <span className="pe-2">Made in India</span>{" "}
        <Image
          src={require("@/assets/images/made-in-india.png")}
          alt="Made in India"
          priority
        />{" "}
      </div>

      <div className="d-flex align-items-center">
        <div className="me-2">
          {wishlistActionLoading && (
            <span>
              <LoadingSpinnerSmall />
            </span>
          )}

          {!wishlistActionLoading && (
            <>
              {wishlistItem ? (
                <a
                  className="pointer"
                  onClick={removeFromWishlist}
                  style={{ color: "#000" }}
                >
                  <FontAwesomeIcon
                    icon={faHeartSolid}
                    width={20}
                    color={"rgb(143, 0, 6)"}
                    style={{ position: "relative", top: -4 }}
                  />
                </a>
              ) : (
                <a
                  className="pointer"
                  onClick={addToWishlist}
                  style={{ color: "#000" }}
                >
                  <FontAwesomeIcon
                    icon={faHeartRegular}
                    width={20}
                    color={"rgba(18, 18, 18, 1)"}
                    style={{ position: "relative", top: -4 }}
                  />
                </a>
              )}
            </>
          )}
        </div>
        <a className="ms-1 pointer" onClick={() => shareProduct("native")}>
          <Image src={share} alt="share" width={22} />
        </a>
      </div>
    </div>
  );
};

export default ProductDetailsMadeInIndiaShare;
